import { APP_ENV } from 'src/global/config/common.config';

import { addBreadcrumb, captureException } from './sentry';

export const unreachable = (): never => {
  throw Error('unreachable method called');
};

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const haveString = (mainString: string, searchStrings: string[]): boolean => {
  return searchStrings.some(searchString => mainString.includes(searchString));
};

export const getOrdinalSuffix = (num: number): string => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = num % 100;
  return num + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const combineHexAndOpacity = (hex: string, opacity: number = 1): string => {
  const cleanHex = hex.replace('#', '');
  const clampedOpacity = Math.min(1, Math.max(0, opacity));

  const alphaHex = Math.round(clampedOpacity * 255)
    .toString(16)
    .padStart(2, '0')
    .toUpperCase();

  return `#${cleanHex.toUpperCase()}${alphaHex}`;
};

export const convertAwsImageUrlToFile = async (imageUrl: string): Promise<File> => {
  try {
    addBreadcrumb(`Fetching image from URL: ${imageUrl}`, 'http', 'info');
    const response = await fetch(imageUrl);

    if (!response.ok) {
      const error = new Error(`Failed to fetch image: ${response.statusText}`);
      captureException(error, { imageUrl, status: response.status });
      throw error;
    }

    const blob = await response.blob();

    const fileName = imageUrl.split('/').pop()?.split('?')[0] ?? 'unknown-file.png';

    // hard code the last modified date to avoid the file being treated as a new file
    return new File([blob], fileName, { type: blob.type, lastModified: new Date('2000-01-01').getTime() });
  } catch (error) {
    captureException(error as Error, { imageUrl });
    throw error;
  }
};

export const getHashFromUrl = (url: string): string | null => {
  const parts = url.split('/ipfs/');
  return parts.length > 1 ? parts[1] : null;
};

export const truncateText = (text: string, maxLength: number) => {
  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength - 3);
  return `${truncated}...`;
};

export const convertToHex = (input: string) => {
  const bytes = new TextEncoder().encode(input);

  const hex = Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

  return `0x${hex}`;
};

export const formatNumWithDots = (number: number) => {
  return new Intl.NumberFormat('en-US').format(number);
};

export const formatNumberWithComma = (value: number) => {
  if (isNaN(value)) return '0';
  return value % 1 === 0
    ? value.toLocaleString('en-US')
    : value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
};

export const generateUrlOtc = (saleAdd: string, otcId: string) => {
  const devUrl = `https://dev-nodesale.lfg.inc/en/${saleAdd}/otc/${otcId}`;
  const stagUrl = `https://stage-client.coresale.lfg.inc/en/${saleAdd}/otc/${otcId}`;
  const prodUrl = `https://nodesale.lfg.inc/en/${saleAdd}/otc/${otcId}`;
  switch (APP_ENV) {
    case 'DEV':
      return devUrl;
    case 'STAGING':
      return stagUrl;
    default:
      return prodUrl;
  }
};
